/**
 * The sidebar (Oct 2026 redesign, direction A: "the pulled tab").
 *
 * Same map as before: the groups, pages and order still come from the Views
 * table through `navSections`, and RBAC decides what is in it. What changed is
 * how it reads and what it carries:
 *
 *  - A warm tinted panel beside the white pages. The page you are on is drawn
 *    in the page's own colour and runs into the page, so "you are here" is a
 *    shape, not a highlight laid on top of a list.
 *  - Groups are quiet headings and always open. Nineteen pages fit on a
 *    laptop screen, so folding a group only ever hid something.
 *  - Collapsed, it is a rail of icons. Hovering or focusing one shows its
 *    name (and why it has a dot); a page with sub pages opens them as links,
 *    so nothing is out of reach in the rail.
 *  - "Needs you" dots, a quick add menu, and your own menu at the foot
 *    (storage, plan, theme, sign out) instead of a meter stacked on the
 *    account row.
 *
 * Everything here is navigation and display. The quick add items only route
 * to a page with `state.quickAdd`, and that page opens its own Add form with
 * its own code; nothing is created from the sidebar.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, PanelLeftClose, PanelLeftOpen, Plus, MoreHorizontal, X,
  HardDrive, Moon, SunMedium, LogOut, Settings as SettingsIcon, CreditCard,
} from 'lucide-react';

const HIDE_DELAY = 140;

function itemState(item, pathname, collapsedSubnavs) {
  const subs = item.subItems || [];
  const hasSub = subs.length > 0;
  const onSection = hasSub
    ? subs.some(s => pathname === s.path || pathname.startsWith(s.path + '/'))
    : false;
  const isActive = onSection || pathname === item.path;
  const subnavOpen = hasSub && onSection && !collapsedSubnavs.has(item.id);
  return { subs, hasSub, onSection, isActive, subnavOpen };
}

export default function Sidebar({
  navSections, pathname, navigate,
  open, mobileOpen, onToggle, onCloseMobile,
  collapsedSubnavs, onToggleSubnav, onOpenSubnav,
  labelFor, signals = {}, quickActions = [],
  account, theme, onToggleTheme, onLogout, settingsPath, guardLeave,
  t,
}) {
  const expanded = open || mobileOpen;
  const asideRef = useRef(null);

  /* ── The floating label / flyout ───────────────────────────────────────
     Lives outside the <aside>: the aside clips its overflow so the width
     can animate, and anything positioned inside it would be cut off at the
     rail's edge. */
  const [hint, setHint] = useState(null);
  const hideTimer = useRef(null);
  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } };
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setHint(null), HIDE_DELAY); };
  useEffect(() => () => cancelHide(), []);
  useEffect(() => { setHint(null); }, [pathname, expanded]);

  const showHint = (e, payload) => {
    cancelHide();
    const r = e.currentTarget.getBoundingClientRect();
    const asideR = asideRef.current ? asideRef.current.getBoundingClientRect() : r;
    setHint({ ...payload, top: r.top + r.height / 2, left: asideR.right + 8 });
  };

  /* ── Quick add and your menu ─────────────────────────────────────────── */
  const [menu, setMenu] = useState(null);   // { kind: 'add' | 'me', rect }
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-sb-menu-trigger]')) return;
      setMenu(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menu]);
  useEffect(() => { setMenu(null); }, [pathname]);

  const toggleMenu = (kind) => (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHint(null);
    setMenu(m => (m && m.kind === kind ? null : { kind, rect: { top: r.top, bottom: r.bottom, left: r.left, right: r.right } }));
  };

  const runQuickAdd = (a) => {
    setMenu(null);
    if (guardLeave && !guardLeave()) return;
    navigate(a.path, { state: { quickAdd: a.kind } });
    if (mobileOpen) onCloseMobile();
  };

  /* A parent with sub pages carries the dot of whichever sub page has one. */
  const signalOf = (item) => {
    if (signals[item.id]) return signals[item.id];
    const subs = (item.subItems || []).map(s => signals[s.id]).filter(Boolean);
    return subs.length ? subs.join('; ') : null;
  };

  const accountSignal = account.planExpired
    ? 'Your plan has expired'
    : account.storage && account.storage.isFull ? 'Storage is full'
      : account.storage && account.storage.isNearFull ? `Storage is ${account.storage.pctLabel} full` : null;

  /* ── One page in the list ────────────────────────────────────────────── */
  const renderItem = (item) => {
    const Icon = item.icon;
    const { subs, hasSub, onSection, isActive, subnavOpen } = itemState(item, pathname, collapsedSubnavs);
    const label = labelFor(item);
    const signal = signalOf(item);
    // The tab goes on the row you can see that IS the page: the sub page when
    // its list is open, otherwise the parent.
    const parentIsTab = isActive && (!hasSub || !subnavOpen || !expanded);

    const onClick = (e) => {
      setHint(null);
      if (hasSub && onSection && expanded) { e.preventDefault(); onToggleSubnav(item.id); }
      else if (hasSub) {
        e.preventDefault();
        onOpenSubnav(item.id);
        // No unsaved-work check here: this is a link, and the app's document
        // level guard has already asked before this handler runs.
        if (subs[0]) navigate(subs[0].path);
      }
    };

    const hintPayload = expanded
      ? (signal ? { label, reason: signal } : null)
      : { label, reason: signal, subs: hasSub ? subs : null, pathname };

    return (
      <React.Fragment key={item.id}>
        <Link
          to={item.path}
          className={`nav-item${parentIsTab ? ' active' : ''}${isActive && !parentIsTab ? ' is-section' : ''}`}
          aria-current={parentIsTab ? 'page' : undefined}
          onClick={onClick}
          onMouseEnter={hintPayload ? (e) => showHint(e, hintPayload) : undefined}
          onMouseLeave={hintPayload ? scheduleHide : undefined}
          onFocus={hintPayload ? (e) => showHint(e, hintPayload) : undefined}
          onBlur={hintPayload ? scheduleHide : undefined}
        >
          <Icon size={17} strokeWidth={isActive ? 2 : 1.75} aria-hidden="true" />
          {expanded ? <span className="nav-label">{label}</span> : <span className="sr-only">{label}</span>}
          {signal && <span className="nav-dot" aria-hidden="true" />}
          {signal && <span className="sr-only">: {signal}</span>}
          {expanded && hasSub && (
            <ChevronDown size={14} className={`nav-chev${subnavOpen ? ' is-open' : ''}`} aria-hidden="true" />
          )}
        </Link>
        {expanded && hasSub && subnavOpen && (
          <div className="nav-subitems">
            {subs.map((sub) => {
              const on = pathname === sub.path;
              const subSignal = signals[sub.id];
              return (
                <Link
                  key={sub.path}
                  to={sub.path}
                  className={`nav-subitem${on ? ' active' : ''}`}
                  aria-current={on ? 'page' : undefined}
                  onMouseEnter={subSignal ? (e) => showHint(e, { label: sub.label, reason: subSignal }) : undefined}
                  onMouseLeave={subSignal ? scheduleHide : undefined}
                >
                  {on && <i className="nav-tick" aria-hidden="true" />}
                  <span className="nav-label">{sub.label}</span>
                  {subSignal && <span className="nav-dot" aria-hidden="true" />}
                  {subSignal && <span className="sr-only">: {subSignal}</span>}
                </Link>
              );
            })}
          </div>
        )}
      </React.Fragment>
    );
  };

  const initials = account.initials;
  const storage = account.storage;

  return (
    <>
      <aside
        ref={asideRef}
        className={`sidebar ${expanded ? 'open' : 'closed'}${mobileOpen ? ' mobile-open' : ''}`}
        aria-label="Main navigation"
      >
        <div className="sidebar-header">
          {expanded ? (
            <>
              {/* The same lockup as the sign-in screen: ink "GEO", accent "8". */}
              <span className="brand-name">GEO <span className="brand-name-accent">8</span></span>
              <button type="button" className="sb-icon-btn" onClick={mobileOpen ? onCloseMobile : onToggle}
                      aria-label={mobileOpen ? 'Close navigation' : 'Collapse sidebar'} title={mobileOpen ? 'Close' : 'Collapse sidebar'}>
                {mobileOpen ? <X size={17} /> : <PanelLeftClose size={17} strokeWidth={1.75} />}
              </button>
            </>
          ) : (
            <>
              <span className="brand-mark" aria-hidden="true">8</span>
              <button type="button" className="sb-icon-btn" onClick={onToggle}
                      aria-label="Expand sidebar"
                      onMouseEnter={(e) => showHint(e, { label: 'Expand sidebar' })} onMouseLeave={scheduleHide}>
                <PanelLeftOpen size={17} strokeWidth={1.75} />
              </button>
              {quickActions.length > 0 && (
                <button type="button" className="sb-add" data-sb-menu-trigger
                        aria-haspopup="menu" aria-expanded={menu?.kind === 'add'} onClick={toggleMenu('add')}
                        onMouseEnter={(e) => { if (!menu) showHint(e, { label: 'Add new' }); }} onMouseLeave={scheduleHide}>
                  <Plus size={15} strokeWidth={2.2} aria-hidden="true" />
                  <span className="sr-only">Add new</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Add new (Oct 2026, round 2): a full-width button with its words on
            it, under the logo and above the first group. The small dark "+"
            in the corner was neither clear nor nice. */}
        {expanded && quickActions.length > 0 && (
          <div className="sb-add-row">
            <button type="button" className={`sb-add-wide${menu?.kind === 'add' ? ' is-open' : ''}`} data-sb-menu-trigger
                    aria-haspopup="menu" aria-expanded={menu?.kind === 'add'} onClick={toggleMenu('add')}>
              <Plus size={16} strokeWidth={2.2} className="sb-add-plus" aria-hidden="true" />
              <span>Add new</span>
              <ChevronDown size={14} className="sb-add-chev" aria-hidden="true" />
            </button>
          </div>
        )}

        <nav className="sidebar-nav">
          {navSections.map((section, i) => {
            if (!section.isGroup) return renderItem(section);
            return (
              <div key={section.id} className="nav-group" role="group" aria-label={section.label}>
                {expanded
                  ? <div className="nav-group-label">{section.label}</div>
                  : (i > 0 && <div className="nav-rail-sep" aria-hidden="true" />)}
                {section.items.map(renderItem)}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button type="button" className={`account-row${menu?.kind === 'me' ? ' is-open' : ''}`}
                  data-sb-menu-trigger aria-haspopup="menu" aria-expanded={menu?.kind === 'me'}
                  onClick={toggleMenu('me')}
                  onMouseEnter={!expanded ? (e) => { if (!menu) showHint(e, { label: account.name, reason: accountSignal }); } : undefined}
                  onMouseLeave={!expanded ? scheduleHide : undefined}>
            <span className="account-avatar" aria-hidden="true">
              {initials}
              {accountSignal && <span className="nav-dot" />}
            </span>
            {expanded ? (
              <>
                <span className="account-info">
                  <span className="account-name" title={account.name}>{account.name}</span>
                  <span className="account-role">
                    <span className="account-role-text">{account.role}</span>
                    {account.planLabel
                      ? <span className="account-plan">{account.planLabel}</span>
                      : account.planExpired ? <span className="account-plan is-expired">{t('expired')}</span> : null}
                  </span>
                </span>
                <MoreHorizontal size={16} className="account-more" aria-hidden="true" />
              </>
            ) : <span className="sr-only">{account.name}</span>}
            {accountSignal && <span className="sr-only">: {accountSignal}</span>}
          </button>
        </div>
      </aside>

      {hint && (
        <div className={`sb-hint${hint.subs ? ' has-subs' : ''}`}
             style={{ top: hint.top, left: hint.left }}
             onMouseEnter={cancelHide} onMouseLeave={scheduleHide}
             role={hint.subs ? 'menu' : 'tooltip'}>
          {hint.subs ? (
            <>
              <div className="sb-hint-title">{hint.label}{hint.reason && <span> · {hint.reason}</span>}</div>
              {hint.subs.map(s => (
                <Link key={s.path} to={s.path} role="menuitem"
                      className={`sb-hint-link${hint.pathname === s.path ? ' active' : ''}`}
                      onClick={() => setHint(null)}>
                  {s.label}{signals[s.id] && <span className="nav-dot" aria-hidden="true" />}
                </Link>
              ))}
            </>
          ) : (
            <span>{hint.label}{hint.reason && <span className="sb-hint-why"> · {hint.reason}</span>}</span>
          )}
        </div>
      )}

      {menu && (
        <div ref={menuRef} className={`sb-menu is-${menu.kind}`} role="menu"
             style={menu.kind === 'add'
               ? { top: menu.rect.bottom + 6, left: Math.max(8, menu.rect.left), minWidth: Math.max(220, menu.rect.right - menu.rect.left),
                   maxHeight: Math.max(220, window.innerHeight - menu.rect.bottom - 18), overflowY: 'auto' }
               : { bottom: Math.max(8, window.innerHeight - menu.rect.top + 6), left: Math.max(8, menu.rect.left) }}>
          {menu.kind === 'add' ? (
            <>
              {/* Grouped like the sidebar (Oct 2026): the same group names in
                  the same order, so "Add driver" is found where Drivers is. */}
              {quickActions.map((a, i) => {
                const Icon = a.icon;
                const newGroup = a.group && (i === 0 || quickActions[i - 1].group !== a.group);
                return (
                  <React.Fragment key={a.kind}>
                    {newGroup && <div className={`sb-menu-title${i > 0 ? ' is-later' : ''}`}>{a.group}</div>}
                    <button type="button" role="menuitem" className="sb-menu-item" onClick={() => runQuickAdd(a)}>
                      <Icon size={15} strokeWidth={1.75} aria-hidden="true" />
                      <span>{a.label}</span>
                    </button>
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            <>
              <div className="sb-menu-who">
                <b>{account.name}</b>
                <span>{account.role}{account.planLabel ? ` · ${account.planLabel}` : ''}</span>
              </div>
              {storage && storage.show && (
                <div className={`sb-menu-meter${storage.isFull ? ' is-full' : storage.isNearFull ? ' is-near' : ''}`} title={storage.title}>
                  <div className="sb-menu-meter-head">
                    <HardDrive size={13} aria-hidden="true" />
                    <span>{storage.text}</span>
                    {storage.hasLimit && <span className="sb-menu-meter-pct">{storage.pctLabel}</span>}
                  </div>
                  {storage.hasLimit && (
                    <div className="progress-bar" role="progressbar" aria-label={t('storageUsedLabel')}
                         aria-valuenow={Math.round(storage.barPct)} aria-valuemin={0} aria-valuemax={100}>
                      <div className="progress-fill" style={{ width: `${storage.barPct}%`, background: storage.color }} />
                    </div>
                  )}
                </div>
              )}
              {account.planExpired && (
                <div className="sb-menu-note is-warn"><CreditCard size={14} aria-hidden="true" />Your plan has expired</div>
              )}
              <div className="sb-menu-sep" />
              <button type="button" role="menuitem" className="sb-menu-item" onClick={() => { onToggleTheme(); }}>
                {theme === 'dark' ? <SunMedium size={15} aria-hidden="true" /> : <Moon size={15} aria-hidden="true" />}
                <span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
              </button>
              {settingsPath && (
                <button type="button" role="menuitem" className="sb-menu-item"
                        onClick={() => { setMenu(null); if (guardLeave && !guardLeave()) return; navigate(settingsPath); }}>
                  <SettingsIcon size={15} strokeWidth={1.75} aria-hidden="true" />
                  <span>Your settings</span>
                </button>
              )}
              <div className="sb-menu-sep" />
              <button type="button" role="menuitem" className="sb-menu-item is-danger" onClick={() => { setMenu(null); onLogout(); }}>
                <LogOut size={15} aria-hidden="true" />
                <span>{t('logout')}</span>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
